// src/hooks/useTheme.js
import { useState, useEffect } from 'react';
import useUser from './useUser';

const THEME_KEY = 'theme';
const LIGHT = 'light';
const DARK = 'dark';

const useTheme = () => {
  const { isLoggedIn, updateProfile, profile } = useUser();
  
  // Get initial theme from localStorage or system preference
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem(THEME_KEY);
    
    // Validate saved theme value
    if (savedTheme === LIGHT || savedTheme === DARK) {
      return savedTheme;
    }
    
    // Clear invalid value if exists
    if (savedTheme) {
      localStorage.removeItem(THEME_KEY);
    }
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return DARK;
    }
    
    return LIGHT;
  };
  
  const [theme, setTheme] = useState(getInitialTheme);
  
  // Load theme from user profile when logged in
  useEffect(() => {
    if (profile?.themePreference) {
      const profileTheme = profile.themePreference;
      const currentTheme = localStorage.getItem(THEME_KEY);
      
      // Only update if profile theme differs from stored theme
      if (profileTheme !== currentTheme) {
        setTheme(profileTheme);
        localStorage.setItem(THEME_KEY, profileTheme);
      }
    }
  }, [profile?.themePreference]);
  
  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
  }, [theme]);
  
  // Toggle theme function
  const toggleTheme = async () => {
    const newTheme = theme === LIGHT ? DARK : LIGHT;
    setTheme(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
    
    // Update user profile if logged in
    if (isLoggedIn) {
      try {
        await updateProfile({ themePreference: newTheme });
      } catch (error) {
        console.error('Failed to update theme preference:', error);
      }
    }
  };
  
  // Set specific theme
  const setSpecificTheme = async (newTheme) => {
    if (newTheme !== LIGHT && newTheme !== DARK) return;
    
    setTheme(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
    
    // Update user profile if logged in
    if (isLoggedIn) {
      try {
        await updateProfile({ themePreference: newTheme });
      } catch (error) {
        console.error('Failed to update theme preference:', error);
      }
    }
  };
  
  return {
    theme,
    toggleTheme,
    setTheme: setSpecificTheme,
    isDark: theme === DARK,
    isLight: theme === LIGHT,
  };
};

export default useTheme;